/**
 * TanStack Query Configuration
 * Centralized configuration for query caching, stale times, and retry logic
 * 
 * @module queryConfig
 */

// ============ Stale Time Configuration ============
// How long data is considered fresh before becoming stale

export const STALE_TIMES = {
  /** Real-time data that changes frequently (10 seconds) */
  realtime: 10_000,
  /** Rapidly changing data like leads, messages (30 seconds) */
  short: 30_000,
  /** Moderately changing data like dashboard stats (1 minute) */
  medium: 60_000,
  /** Slowly changing data like user profiles (5 minutes) */
  long: 5 * 60_000,
  /** Nearly static data like settings, feature flags (10 minutes) */
  veryLong: 10 * 60_000,
  /** Static reference data like countries, categories (30 minutes) */
  static: 30 * 60_000,
  /** Infinite - never becomes stale automatically */
  infinite: Infinity,
} as const;

// ============ Garbage Collection Time Configuration ============
// How long unused/inactive cache entries are kept in memory

export const GC_TIMES = {
  /** Very short-lived cache (1 minute) */
  short: 60_000,
  /** Default cache lifetime (5 minutes) */
  default: 5 * 60_000,
  /** Extended cache for slower changing data (15 minutes) */
  extended: 15 * 60_000,
  /** Long-lived cache for reference data (30 minutes) */
  long: 30 * 60_000,
  /** Very long cache for static data (1 hour) */
  veryLong: 60 * 60_000,
} as const;

// ============ Retry Configuration ============

export const RETRY_CONFIG = {
  /** No retries for mutations */
  mutation: 0,
  /** Single retry for queries */
  query: 1,
  /** Extended retries for critical data */
  critical: 3,
} as const;

// ============ Query Options Presets ============
// Pre-configured query options for common use cases

export const queryPresets = {
  /**
   * Real-time data that updates frequently.
   * With WebSockets handling push updates, polling is only a fallback.
   * Example: Live leaderboards, active sessions, notifications
   */
  realtime: {
    staleTime: STALE_TIMES.realtime,
    gcTime: GC_TIMES.short,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  },

  /**
   * Dynamic list data that changes regularly
   * Example: Leads pipeline, student lists, task queues
   */
  dynamicList: {
    staleTime: STALE_TIMES.short,
    gcTime: GC_TIMES.default,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  },

  /**
   * Dashboard/analytics data
   * Example: KPIs, charts, aggregations
   */
  dashboard: {
    staleTime: STALE_TIMES.medium,
    gcTime: GC_TIMES.extended,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  },

  /**
   * User profile and settings data
   * Example: User details, preferences, permissions
   */
  profile: {
    staleTime: STALE_TIMES.long,
    gcTime: GC_TIMES.long,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  },

  /**
   * Filter options and dropdown data
   * Example: Course lists, branch options, status enums
   */
  filterOptions: {
    staleTime: STALE_TIMES.long,
    gcTime: GC_TIMES.long,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  },

  /**
   * Static reference data
   * Example: Countries, categories, feature flags
   */
  static: {
    staleTime: STALE_TIMES.static,
    gcTime: GC_TIMES.veryLong,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  },

  /**
   * Infinite scroll / paginated data
   * Example: History logs, message threads, infinite lists
   */
  infinite: {
    staleTime: STALE_TIMES.short,
    gcTime: GC_TIMES.extended,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    maxPages: 10, // Keep max 10 pages in cache
  },

  /**
   * Form data that should persist during editing
   * Example: Pre-filled form values, draft data
   */
  formData: {
    staleTime: STALE_TIMES.veryLong,
    gcTime: GC_TIMES.extended,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  },
} as const;

// ============ Query Key Factories ============
// Consistent query key generation for cache management

export const createQueryKeyFactory = <T extends string>(namespace: T) => ({
  all: [namespace] as const,
  lists: () => [namespace, 'list'] as const,
  list: (filters: Record<string, unknown>) => [namespace, 'list', filters] as const,
  details: () => [namespace, 'detail'] as const,
  detail: (id: string | number) => [namespace, 'detail', id] as const,
  infinite: (filters: Record<string, unknown>) => [namespace, 'infinite', filters] as const,
});

// ============ Default Query Client Options ============

export const defaultQueryClientOptions = {
  queries: {
    staleTime: STALE_TIMES.medium,
    gcTime: GC_TIMES.default,
    retry: RETRY_CONFIG.query,
    placeholderData: (previousData: unknown) => previousData,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchOnMount: true,
    networkMode: 'online' as const,
  },
  mutations: {
    retry: RETRY_CONFIG.mutation,
    networkMode: 'online' as const,
  },
};

// ============ Type Exports ============

export type StaleTimeKey = keyof typeof STALE_TIMES;
export type GCTimeKey = keyof typeof GC_TIMES;
export type QueryPresetKey = keyof typeof queryPresets;
